import { CalendarClock, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { urlTransform } from "@/components/feature/markdown/urlTransform";

import { useDateFormatter } from "@/hooks/useDateFormatter";
import { cn } from "@/lib/utils";
import type { ProcurementStatus, SupplierContractValueAggregate } from "@/types/supplier";

import { formatContractValue } from "./contract-value-format";

interface CategoryProps {
  category?: string | null;
  className?: string;
}

export const ContractFamilyCategory = ({ category, className }: CategoryProps) =>
  category ? (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md border border-[#ddd5cb] bg-[#f7f2ec] px-2 py-0.5 text-xs font-semibold text-[#111b35]",
        className,
      )}
      title={category}
    >
      <Tag className="size-3.5 flex-shrink-0 text-desert-sand-700" aria-hidden="true" />
      <span className="truncate">{category}</span>
    </span>
  ) : (
    <span className={cn("text-xs font-medium text-gray-400", className)}>No category</span>
  );

interface ExpiryProps {
  expiryDate?: string | null;
  status?: ProcurementStatus | string | null;
  className?: string;
}

const expiryTone = (status: ExpiryProps["status"]) => {
  if (status === "expired") return "text-[#9e3025]";
  if (status === "expiring") return "text-[#805515]";
  return "text-gray-700";
};

export const ContractFamilyExpiry = ({ expiryDate, status, className }: ExpiryProps) => {
  const { formatDate } = useDateFormatter();
  if (!expiryDate) return <span className={cn("text-xs font-medium text-gray-400", className)}>No expiry date</span>;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-semibold", expiryTone(status), className)}>
      <CalendarClock className="size-3.5 flex-shrink-0" aria-hidden="true" />
      {status === "expired" ? "Expired" : "Expires"} {formatDate(expiryDate)}
    </span>
  );
};

interface GbpEquivalentProps {
  value?: string | null;
  currencyCode?: string | null;
  className?: string;
}

/** The GBP value beside a contract value held in another currency. */
export const GbpEquivalent = ({ value, currencyCode, className }: GbpEquivalentProps) =>
  value && currencyCode && currencyCode !== "GBP" ? (
    <span className={cn("block text-xs font-medium text-gray-500", className)}>
      ≈ {formatContractValue(value, "GBP")}
    </span>
  ) : null;

interface ContractValueProps {
  value?: string | null;
  currencyCode?: string | null;
  /** Shown instead of "Not set" when there is no value with a currency. */
  fallback?: string | null;
}

/** A contract value in its currency; a value is shown only together with its currency. */
export const ContractValue = ({ value, currencyCode, fallback }: ContractValueProps) =>
  value && currencyCode ? formatContractValue(value, currencyCode) : (fallback ?? "Not set");

interface SummaryDateProps {
  date: string;
}

export const SummaryDate = ({ date }: SummaryDateProps) => {
  const { formatDate } = useDateFormatter();
  return <>{formatDate(date)}</>;
};

interface MissingGbpRateNoteProps {
  className?: string;
}

/** Marks a GBP sum that leaves out values whose currency has no GBP rate. */
export const MissingGbpRateNote = ({ className }: MissingGbpRateNoteProps) => (
  <span className={cn("text-xs font-medium text-gray-500", className)}>(excludes values without a GBP rate)</span>
);

interface GbpTotalProps {
  value?: string;
  contractValues: SupplierContractValueAggregate;
}

/** All currencies of a supplier summed in GBP; shown only when a non-GBP value exists. */
export const GbpTotal = ({ value, contractValues }: GbpTotalProps) => {
  if (value === undefined || contractValues.currencies.every(total => total.currency_code === "GBP")) return null;
  return (
    <dd className="mt-1 text-sm font-semibold tabular-nums text-gray-600">
      ≈ {formatContractValue(value, "GBP")} in GBP
      {contractValues.gbp_complete === false ? <MissingGbpRateNote className="ml-1.5" /> : null}
    </dd>
  );
};

interface SummaryTextProps {
  text: string;
  className?: string;
}

/** Contract summary text written by the model: markdown, rendered without raw HTML or images. */
export const SummaryText = ({ text, className }: SummaryTextProps) => (
  <div className={cn("space-y-1.5 break-words", className)}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      urlTransform={urlTransform}
      disallowedElements={["img"]}
      components={{
        p: ({ node: _node, ...props }) => <p {...props} />,
        ul: ({ node: _node, ...props }) => <ul className="list-disc space-y-1 pl-5" {...props} />,
        ol: ({ node: _node, ...props }) => <ol className="list-decimal space-y-1 pl-5" {...props} />,
        strong: ({ node: _node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
        a: ({ node: _node, ...props }) => (
          <a className="underline" target="_blank" rel="noopener noreferrer nofollow" {...props} />
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  </div>
);
